import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from 'src/environments/environment';
import { format } from 'date-fns';
import { map } from 'rxjs/operators';
import { IBuilding, IBuildingWithDistance, IRoom } from '../../../../components/map/conjectfm.service';
import { IPOI } from '../../../../components/map/poi.service';
import { v5 as uuidv5 } from 'uuid';
import { TranslateService } from '@ngx-translate/core';

const uuidNS = uuidv5('study-spaces.app.itmc.tu-dortmund.de', uuidv5.DNS);

const API_URL = environment.apiUrl;
const API_STUDY_SPACES = `${API_URL}/study-spaces/v1`;
// const API_STUDY_SPACES = 'http://localhost:8090';
const API_HOLIDAYS = `${API_STUDY_SPACES}/holidays`;
const API_ROOMS = `${API_STUDY_SPACES}/rooms`;
const API_ROOMS_DETAILS = `${API_ROOMS}/{0}/{1}`;
const API_BUILDINGS = `${API_STUDY_SPACES}/buildings`;
const API_BUILDINGS_WITH_DISTANCE = `${API_BUILDINGS}/distance`;

export interface Holiday {
  name: {
    de: string;
    en: string;
  };
  date: string;
}

export interface RoomData {
  holiday: Holiday;
  appointments: RoomAppointment[];
  room: IRoom;
}

export interface RoomAppointment {
  eventNumber: string;
  title: string;
  start: Date;
  end: Date;
  rhythmId: number;
}

export enum ApointmentRhythm {
    UNKNOWN = 0,
    WEEKLY = 3,
    BI_WEEKLY = 8,
    SINGLE = 9,
    BLOCK = 12,
    N_V = 14,
    TRI_WEEKLY = 15,
    QUAD_WEEKLY = 16,
    NOT_SPECIFIED = 17,
    EVEN_WEEK = 18,
    ODD_WEEK = 19,
    BLOCK_PLUS_SATURDAY = 20,
    BLOCK_PLUS_SATURDAY_SUNDAY = 21,
}

export interface AppointmentRoom {
  room: IRoom;
  currentlyBlocked: boolean;
  currentAppointment: RoomAppointment
}

@Injectable({
  providedIn: 'root'
})
export class StudySpacesService {
  private http = inject(HttpClient);
  private translate = inject(TranslateService);


  public holidays(): Observable<Holiday[]> {
    return this.http.get<Holiday[]>(API_HOLIDAYS);
  }

  public isHoliday(date: Date): Observable<boolean> {
    return this.http.get<boolean>(`${API_HOLIDAYS}/${format(date, 'yyyy-MM-dd')}`);
  }

  public room(roomId: number, date: Date): Observable<RoomData> {
    return this.http.get<RoomData>(API_ROOMS_DETAILS.format(roomId, format(date, 'yyyy-MM-dd')))
      .pipe(map((result: RoomData): RoomData => ({
        ...result,
        appointments: result.appointments.map((appointment: RoomAppointment): RoomAppointment => ({
          ...appointment,
          start: new Date(appointment.start),
          end: new Date(appointment.end),
        }))
      })));
  }

  public getBuildings(): Observable<IBuilding[]> {
    return this.http.get<IBuilding[]>(API_BUILDINGS);
  }

  public getRooms(): Observable<IRoom[]> {
    return this.http.get<IRoom[]>(API_ROOMS);
  }

  public getRoomsAsPOIs(): Observable<IPOI[]> {
    return this.getRooms().pipe(map((rooms: IRoom[]): IPOI[] => {
      return rooms.map((room: IRoom): IPOI => {
        const uuid = uuidv5(`${room.id}`, uuidNS);
        
        return {
          id: uuid,
          name: {
            de: room.building.name,
            en: room.building.name
          },
          description: null,
          roomId: room.id,
          room: {
            ...room,
            building: {
              ...room.building
            }
          },
          /*
           * categoryId 9 was selected since these are basically "Lernorte" 
           */
          categoryId: 9,
          gps: room.building.gps,
          studySpace: true
        };
      });
    }));
  }

  public getBuildingsAsPOIs(): Observable<IPOI[]> {
    return this.getBuildings().pipe(map((buildings: IBuilding[]): IPOI[] => {
      return buildings.map((building: IBuilding): IPOI => ({
        id: `${building.id}`,
        name: {
          de: building.name,
          en: building.name
        },
        description: {
          de: building.description,
          en: building.description
        },
        // categoryId: 9 -> Lernorte
        categoryId: 9,
        gps: building.gps,
        studySpace: true
      }));
    }));
  }

  public getRoomsByBuilding(buildingId: number): Observable<AppointmentRoom[]> {
    return this.http.get<AppointmentRoom[]>(`${API_BUILDINGS}/${buildingId}/rooms`);
  }

  public buildingsWithDistance(latitude: number, longitude: number, elevation: number = 0.0, threshold: number = 50.0): Observable<IBuildingWithDistance[]> {
    return this.http.get<IBuildingWithDistance[]>(
      API_BUILDINGS_WITH_DISTANCE,
      { params: new HttpParams()
          .set('lat', latitude)
          .set('lng', longitude)
          .set('elevation', elevation)
          .set('threshold', threshold)
      }
    );
  }

  getRhythmDescription(rhythm: ApointmentRhythm): string {
    const key = this.getRhythmTranslationKey(rhythm);
    return this.translate.instant(key);
  }

  private getRhythmTranslationKey(rhythm: ApointmentRhythm): string {
    const keys: Record<ApointmentRhythm, string> = {
        [ApointmentRhythm.UNKNOWN]: 'study-areas.studySpaces.rhythm.unknown',
        [ApointmentRhythm.WEEKLY]: 'study-areas.studySpaces.rhythm.weekly',
        [ApointmentRhythm.BI_WEEKLY]: 'study-areas.studySpaces.rhythm.biWeekly',
        [ApointmentRhythm.SINGLE]: 'study-areas.studySpaces.rhythm.single',
        [ApointmentRhythm.BLOCK]: 'study-areas.studySpaces.rhythm.block',
        [ApointmentRhythm.N_V]: 'study-areas.studySpaces.rhythm.nv',
        [ApointmentRhythm.TRI_WEEKLY]: 'study-areas.studySpaces.rhythm.triWeekly',
        [ApointmentRhythm.QUAD_WEEKLY]: 'study-areas.studySpaces.rhythm.quadWeekly',
        [ApointmentRhythm.NOT_SPECIFIED]: 'study-areas.studySpaces.rhythm.notSpecified',
        [ApointmentRhythm.EVEN_WEEK]: 'study-areas.studySpaces.rhythm.evenWeek',
        [ApointmentRhythm.ODD_WEEK]: 'study-areas.studySpaces.rhythm.oddWeek',
        [ApointmentRhythm.BLOCK_PLUS_SATURDAY]: 'study-areas.studySpaces.rhythm.blockPlusSaturday',
        [ApointmentRhythm.BLOCK_PLUS_SATURDAY_SUNDAY]: 'study-areas.studySpaces.rhythm.blockPlusSaturdaySunday',
    };
    return keys[rhythm] ?? 'study-areas.studySpaces.rhythm.unknown';
  }
}
